import { useCallback, useEffect, useRef, useState } from 'react'
import type { MathfieldElement } from 'mathlive'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { BlockContent } from '~/components/BlockContent'
import Input from '~/bootcamp/Activities/components/Input'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { FillInTheBlankInputType } from '~/types/bootcamp'
import bootcampStyle from '~/shared/styles/BootcampSanityBlock.module.css'
import { useAppSelector } from '~/redux/hooks'
import { selectCorrectConsecutiveAnswers } from '~/bootcamp/slices/bootcamp-activity.slice'
import { AppLanguage } from '~/types/locale'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import { FillInTheBlankActivityData, FillInTheBlankProps } from './FillInTheBlank.type'
import { checkAnswer, generateFillInTheBlanksActivityData } from './FillInTheBlank.util'

function FillInTheBlank({
  answerState,
  onNextQuestionClick,
  onAnswerStateChange,
  onCurrentQuestionChange,
  actionButtonsFC,
  questions,
  keyboardLayoutId = null,
  autofocus = true,
}: FillInTheBlankProps): React.ReactElement {
  const [answers, setAnswers] = useState<string[]>(new Array(questions[0].inputs.length).fill(''))
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [inputsState, setInputsState] = useState<boolean[]>([])
  const [questionHistory, setQuestionHistory] = useState<number[]>([])
  const [activityData, setActivityData] = useState<FillInTheBlankActivityData>({
    question: questions[0],
    questionIndex: 0,
  })
  const { t } = useTranslation('generics')
  const [inputRef, setInputRef] = useState<{ current: HTMLInputElement | null }>({ current: null })
  const correctConsecutiveAnswers = useAppSelector(selectCorrectConsecutiveAnswers)
  const locale = useRouter().locale

  const refCallback = useCallback((node: HTMLInputElement | null) => {
    setInputRef({ current: node })
  }, [])

  useFocus(autofocus, !!answerState, inputRef)

  const keyboardInputRef = useRef<MathfieldElement>(null)
  useEffect(() => {
    if (autofocus && keyboardInputRef.current) {
      if (correctConsecutiveAnswers === 3) keyboardInputRef.current.blur()
    }
  }, [autofocus, correctConsecutiveAnswers, keyboardInputRef])

  useEffect(() => {
    const { question, questionIndex } = generateFillInTheBlanksActivityData(questions, questionHistory)
    setAnswers(new Array(question.inputs.length).fill(''))
    setActivityData({ question, questionIndex })
  }, [questionHistory, questions])

  // Keyed on the questionId string: the effect above returns a fresh activityData object every run.
  useEffect(() => {
    if (activityData.question.questionId) onCurrentQuestionChange?.(activityData.question.questionId)
  }, [activityData.question.questionId, onCurrentQuestionChange])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answers, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const tempInputsState = checkAnswer(activityData.question, answers)
    setInputsState(tempInputsState)
    const value = tempInputsState.filter((state) => state).length === activityData.question.inputs.length
    // In on-screen input order: the backend pairs them positionally with the blanks.
    onAnswerStateChange(value, activityData.question.questionId, undefined, {
      type: 'fill_in',
      values: answers,
    })
  }

  const setInputAnswer = (index: number, value: string): void => {
    setAnswers((prevAnswer) => {
      const tempAnswer = JSON.parse(JSON.stringify(prevAnswer))
      if (activityData.question.inputs[index].inputType === FillInTheBlankInputType.INTEGER) {
        value = value.replace(/[,.]/g, '')
      } else if (activityData.question.inputs[index].inputType === FillInTheBlankInputType.FLOAT) {
        if (locale === AppLanguage.NO) {
          value = value.replace(/[.]/g, ',')
        } else {
          value = value.replace(/[,]/g, '.')
        }
      }
      tempAnswer[index] = value
      return tempAnswer
    })
  }

  const handleOnNextQuestionClick = (): void => {
    setQuestionHistory((prevState) => {
      return [activityData.questionIndex, ...prevState].slice(0, questions.length - 1)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }
  const resetActivity = (): void => {
    const resetAnswers = new Array(activityData.question.inputs.length).fill('')

    setAnswers(resetAnswers)
    keyboardInputRef?.current?.setValue(resetAnswers[0])
    keyboardInputRef?.current?.focus()
  }

  const onEnterPress = (): void => {
    if (answers.filter((a) => a !== '').length > 0) {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <div className="my-auto py-8 md:py-0 w-full flex-col flex-wrap justify-center items-center text-purple font-semibold text-md">
        <div className="text-center md:mt-4 md:mb-8">
          <BlockContent
            blocks={activityData.question.question}
            className={`${bootcampStyle['bootcamp-block-content']}`}
            renderContainerOnSingleChild
          />
          <div className="flex justify-center flex-wrap gap-x-4">
            {activityData.question.inputs.map(({ label, _key, inputType }, index) => (
              <div className="flex items-center gap-2 mt-3" key={_key}>
                <BlockContent
                  blocks={label}
                  className={`${bootcampStyle['bootcamp-block-content']}`}
                  renderContainerOnSingleChild
                />
                <Input
                  aria-label={t('ariaLabelButtons.insertResult')}
                  autoFocus={index === 0}
                  className="max-w-20 md:max-w-30"
                  isError={answerState === false && inputsState[index] !== true}
                  key={inputKey}
                  keyboardLayoutId={keyboardLayoutId}
                  onChange={(value) => setInputAnswer(index, value)}
                  onEnter={onEnterPress}
                  ref={index === 0 ? refCallback : null}
                  type={inputType === FillInTheBlankInputType.INTEGER ? 'number' : 'text'}
                  value={answers[index]}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answers.filter((a) => a !== '').length > 0,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: resetActivity,
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default FillInTheBlank
